import React, { useContext, useMemo } from 'react';
import EnhancedChart from './MultiChart';
import { Typography } from '@mui/material';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import {
  appointmentTypeColor,
  appointmentTypeLabel,
  AppointmentTypeRef,
  normalizeDepartmentTypePercentages,
} from '../utils/outpatientAppointmentTypes';

export interface IAppointmentTypeData {
  appointmentType: AppointmentTypeRef;
  departmentUuid: string;
  departmentName: string;
  totalAppointments: number;
  avgDaysUntilAppointment: number;
  minDaysUntilAppointment: number;
  maxDaysUntilAppointment: number;
  mostCommonBookingMonth: string;
  mostCommonAppointmentMonth: string;
  mostCommonBookingDay: number;
  mostCommonAppointmentDay: number;
}

interface AppointmentTypeChartProps {
  data: IAppointmentTypeData[];
  /** The series, in the order the API sent them (SHER-1049). */
  appointmentTypes: AppointmentTypeRef[];
  title?: string;
  chartType?: 'bar' | 'pie' | 'donut';
  stacked?: boolean;
  horizontal?: boolean;
}

const AppointmentTypeChart = ({ 
  data,
  appointmentTypes,
  title,
  chartType = 'bar',
  stacked = true,
  horizontal = false
}: AppointmentTypeChartProps) => {
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
  // package's typings. See analyticsTranslate.sher828.test.tsx.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);
  
  // Transform data for the chart
  const chartData = useMemo(() => {
    const departments = [...new Set(data.map(item => item.departmentName))].sort();

    const countFor = (dept: string, type: AppointmentTypeRef) => {
      const match = data.find(
        (item) => item.departmentName === dept && item.appointmentType.key === type.key,
      );
      return match ? match.totalAppointments : 0;
    };

    const departmentPercentages = departments.map((dept) => {
      const counts = appointmentTypes.map((type) => countFor(dept, type));
      return normalizeDepartmentTypePercentages(counts);
    });

    // The payload is matched on the type's key; what the legend shows is the short
    // translated form, and the colour rides along because that displayed name no longer
    // matches anything in categoryColorMap.
    const series = appointmentTypes.map((type, typeIndex) => ({
      name: appointmentTypeLabel(type, translate, 'short'),
      color: appointmentTypeColor(type),
      data: departmentPercentages.map((percentages) => percentages[typeIndex]),
    }));

    const totalAppointments = data.reduce((sum, item) => sum + item.totalAppointments, 0);
    const departmentTotals = departments.reduce((acc, dept) => {
      acc[dept] = appointmentTypes.reduce(
        (sum, type) => sum + countFor(dept, type),
        0,
      );
      return acc;
    }, {} as Record<string, number>);

    return {
      series,
      departments,
      totalAppointments,
      departmentTotals,
      appointmentTypes,
    };
  }, [data, appointmentTypes, translate]);
  
  // Create percentage-based data for pie/donut charts
  const percentageData = useMemo(() => {
    if (!['pie', 'donut'].includes(chartType)) {
      return null;
    }
    
    // For pie/donut charts, we'll group by appointment type across all departments
    const totalAppointments = chartData.totalAppointments;
    
    // Sum appointments by type and convert to percentages
    const typeData = appointmentTypes.map(type => {
      const sum = data
        .filter(item => item.appointmentType.key === type.key)
        .reduce((total, item) => total + item.totalAppointments, 0);
      
      // Calculate percentage of total
      const percentage = totalAppointments > 0 ? (sum / totalAppointments * 100) : 0;
      
      return {
        name: `${appointmentTypeLabel(type, translate, 'short')} (${percentage.toFixed(1)}%)`,
        data: [percentage] // Percentage value for pie chart
      };
    });
    
    return {
      series: typeData,
      categories: ['All Departments'],
      total: 100 // Now the total is 100%
    };
  }, [data, appointmentTypes, chartType, chartData.totalAppointments, translate]);

  // Format title component
  const titleComponent = (
    <Typography variant="h6">
      {title} {chartType !== 'pie' && chartType !== 'donut' ? '(%)' : ''}
    </Typography>
  );

  // Choose the right data based on chart type
  const chartProps = ['pie', 'donut'].includes(chartType)
    ? {
        series: percentageData?.series || [],
        title: titleComponent,
        categories: percentageData?.categories || [],
        type: chartType,
        total: percentageData?.total
      }
    : {
        series: chartData.series,
        title: titleComponent,
        categories: chartData.departments,
        type: chartType,
        stacked,
        horizontal,
        total: chartData.totalAppointments
      };

  return <EnhancedChart {...chartProps} />;
};

export default AppointmentTypeChart;